import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import Logo from "../../../components/Assests/Phibro-Tagline-Logo.png";
import HelpInstructionImage from "../../../components/Assests/Help-Instruction-Image.png";
import "./PrintPreview.css";
import {
  getShippingDetails,
  printSamplesPDF,
  formatShippingData,
  getShippingDetailsBySample
} from "./PrintPreviewApi";

const Barcode = ({ base64Data, barcodeNumber }) => (
  <div className="barcode-img-wrap">
    {base64Data ? (
      <img
        src={`data:image/png;base64,${base64Data}`}
        alt="barcode"
        className="barcode-img"
      />
    ) : (
      <img
        src={`https://barcodeapi.org/api/128/${encodeURIComponent(
          barcodeNumber
        )}`}
        alt="barcode"
        className="barcode-img"
      />
    )}
  </div>
);

const HelpPopup = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="help-popup-overlay">
      <div className="help-popup">
        <div className="help-popup-header">
          <div className="help-popup-title">Printing and packaging</div>
          <button
            onClick={onClose}
            className="help-popup-close"
            aria-label="Close help popup"
            title="Close help popup"
          >
            ×
          </button>
        </div>
        <hr className="help-popup-divider" />
        <div className="help-popup-img-row">
          <img
            src={HelpInstructionImage}
            alt="Printing and packaging steps"
            className="help-popup-img"
          />
        </div>
        <div className="help-popup-footer">
          <button onClick={onClose} className="help-popup-close-btn" title="Close help popup">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const PrintPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isFromAssayRequestRecord = location.pathname.includes('/assay-request-record/print-preview');
  const isCompanyManagement = location.pathname.includes('/company-management/print-preview');

  const [helpOpen, setHelpOpen] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    const hasIdParams = urlParams.has('id');
    return !(isFromAssayRequestRecord && hasIdParams);
  });

  const [sampleData, setSampleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupIds, setGroupIds] = useState([]);

  useEffect(() => {
    const fetchShippingDetails = async () => {
      try {
        setLoading(true);
        const urlParams = new URLSearchParams(location.search);

        let groupIdsToUse = [];

        if (isCompanyManagement) {
          const urlGroupIds = urlParams.getAll('groupId');
          const urlIds = urlParams.getAll('id');
          groupIdsToUse = urlGroupIds.length > 0 ? urlGroupIds : (urlIds.length > 0 ? urlIds : []);
        } else {
          groupIdsToUse = urlParams.getAll('groupId');
        }

        if (groupIdsToUse.length > 0) {
          setGroupIds(groupIdsToUse);

          const promises = groupIdsToUse.map(async (id) => {
            const response = await getShippingDetails(id);
            return formatShippingData(response);
          });

          const responses = await Promise.all(promises);
          const allSampleData = responses.flat();
          setSampleData(allSampleData);
        } else {
          const urlGroupId = urlParams.get("groupId") || urlParams.get("sampleId");
          if (urlGroupId) {
            const stateGroupId = location.state?.groupId;
            const finalGroupId = urlGroupId || stateGroupId;
            if (finalGroupId) {
              setGroupIds([finalGroupId]);
              const response = await getShippingDetailsBySample(finalGroupId);
              const formattedData = formatShippingData(response);
              setSampleData(formattedData);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch shipping details:", error);
        setSampleData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchShippingDetails();
  }, [location.pathname, location.search, isFromAssayRequestRecord, isCompanyManagement]);

  const handleEdit = (sampleId) => {
    if (isFromAssayRequestRecord) {
      navigate(`/assay-request-record?sampleId=${sampleId}`);
    } else {
      navigate(`/request-feed-assay?sampleId=${sampleId}`);
    }
  };

  const handlePrint = async () => {
    try {
      if (sampleData.length > 0) {
        setLoading(true);
        await printSamplesPDF(sampleData, groupIds);
      }
    } catch (error) {
      console.error("Print failed:", error);
      alert("Failed to print. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBreadcrumbNavigation = () => {
    if (isFromAssayRequestRecord) {
      navigate("/assay-request-record");
    } else {
      navigate("/request-feed-assay");
    }
  };

  const handleCancel = () => {
    if (isFromAssayRequestRecord) {
      navigate("/assay-request-record");
    } else if (isCompanyManagement) {
      navigate("/company-management");
    } else {
      navigate("/request-feed-assay");
    }
  };

  return (
    <div>
      <div className="print-preview-container">
        <div className="breadcrumb-row">
          <button
            className="breadcrumb-link"
            type="button"
            onClick={handleBreadcrumbNavigation}
            title="Navigate back"
          >
            {isFromAssayRequestRecord ? "Assay Request Record" : "Request Feed Assay"}
          </button>
          <span className="slash">/</span>
          <span className="breadcrumb-current">Print Preview</span>
          <button
            className="help-btn"
            type="button"
            onClick={() => setHelpOpen(true)}
            title="View printing and packaging help"
          >
            Help
          </button>
        </div>

        <div className="instructions-row">
          <div className="instruction-box">
            <b>Packing Instructions</b>
            <ul>
              <li>
                Per 21 CFR 225.58(b)(1): only one active ingredient need to be
                tested on combination products
              </li>
              <li>Samples are Assayed As Received</li>
              <li>Only one sample per envelope</li>
              <li>Use of sample bags provided by laboratory preferred</li>
              <li>No Staples to seal envelope please</li>
            </ul>
          </div>
          <div className="vertical-divider" />
          <div className="instruction-box">
            <b>Printing Instructions</b>
            <ul>
              <li>
                There are a maximum of 6 bar codes per page. Please ensure you
                do not print double sided if you have more than 6 samples.
              </li>
            </ul>
          </div>
        </div>

        <div className="shipping-details-card" id="shipping-details-print">
          <div className="shipping-top-row">
            <img src={Logo} alt="Phibro Logo" className="shipping-logo" />
            <div className="shipping-header">Shipping Details</div>
          </div>
          <div className="samples-wrapper">
            <div className="samples-scroll-area">
              {sampleData.length > 0 ? (
                sampleData.map((s, idx) => (
                  <div className="sample-row-grid" key={s.sampleId || idx}>
                    <div className="sample-left">
                      <div className="sample-meta-heading">
                        Sample {idx + 1} of {sampleData.length}
                      </div>
                      <div className="meta-line">
                        <span className="meta-label-inline">Sample ID:</span>
                        <span className="meta-value-inline">{s.sampleId}</span>
                      </div>
                      <div className="meta-line">
                        <span className="meta-label-inline">Group ID:</span>
                        <span className="meta-value-inline">{s.groupId}</span>
                      </div>
                      <div className="meta-line meta-line-custid">
                        <span className="meta-label-inline">Cust ID:</span>
                        <span className="meta-value-inline">{s.controlId}</span>
                      </div>
                      <Barcode
                        base64Data={s.base64Barcode}
                        barcodeNumber={s.barcodeNumber || s.sampleId}
                      />
                    </div>
                    <div className="sample-meta-table">
                      <div className="label">Company Name</div>
                      <div className="colon">:</div>
                      <div className="value">{s.companyName || "-"}</div>
                      <div className="label">Location</div>
                      <div className="colon">:</div>
                      <div className="value">{s.location || "-"}</div>
                      <div className="label">Unit</div>
                      <div className="colon">:</div>
                      <div className="value">{s.unit || "-"}</div>
                      <div className="label">Expected Level</div>
                      <div className="colon">:</div>
                      <div className="value">{s.specialLevel || "-"}</div>
                      <div className="label">Species</div>
                      <div className="colon">:</div>
                      <div className="value">{s.species || "-"}</div>
                      <div className="label">Product Type</div>
                      <div className="colon">:</div>
                      <div className="value">{s.productType || "-"}</div>
                      <div className="label">Lot No.</div>
                      <div className="colon">:</div>
                      <div className="value">{s.lotNo || "-"}</div>
                    </div>
                    <button
                      className="edit-inline-btn"
                      type="button"
                      onClick={() => handleEdit(s.sampleId)}
                      title="Edit sample"
                    >
                      Edit
                    </button>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <p>No sample data available</p>
                </div>
              )}
            </div>
          </div>
          <div className="action-buttons-row">
            <button
              className="action-btn"
              type="button"
              onClick={handleCancel}
              title="Cancel and go back"
            >
              Cancel
            </button>
            <button
              className="action-btn print"
              type="button"
              onClick={handlePrint}
              disabled={loading || sampleData.length === 0}
              title="Print shipping details"
            >
              Print
            </button>
          </div>
        </div>
      </div>
      <HelpPopup open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};

export default PrintPreview;
